import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom, timeout } from 'rxjs';

import type { PersistedTokenBagEntry } from '@vevents/auth';

import { TokenBagService } from './token-bag.service';
import type {
  ApiEnvelope,
  RefreshResponse
} from '../models/auth.models';
import type {
  MergedYourEventsRow,
  YourEventsResponse,
  YourEventsSegment
} from '../models/your-events.models';
import { environment } from '../../../environments/environment';

/**
 * MOB-100 Stage 18 — client-side parallel fan-out for the per-tenant
 * `GET /api/mobile/your-events` endpoint (Stage 7).
 *
 * Per Stage 7's architectural-guidance the server explicitly refuses
 * cross-tenant aggregation. The mobile app therefore:
 *  1. Looks at every entry in the token bag.
 *  2. Fires one request per tenant in parallel against that tenant's
 *     subdomain (per the platform's `{slug}.myvirtrio.cloud` convention).
 *  3. Merges results client-side into a flat segment-keyed list.
 *  4. Marks any tenants whose call failed as `tenantUnavailable: true`
 *     so the merged list renders a graceful per-tenant error chip.
 *
 * The per-tenant request uses the tenant's bearer token. We pick the
 * token by tenant id rather than `AuthService.getToken()` so the fan-out
 * works regardless of which tenant is currently "active" in the UI.
 *
 * We do not call `refresh()` here — the HTTP `authInterceptor` handles
 * 401s with the standard queue/refresh/replay flow.
 */
@Injectable({ providedIn: 'root' })
export class YourEventsService {
  private readonly http = inject(HttpClient);
  private readonly tokenBag = inject(TokenBagService);

  readonly isLoading = signal(false);
  readonly rows = signal<MergedYourEventsRow[]>([]);
  /** Per-tenant unavailable flags surfaced via the merged-row metadata. */
  readonly tenantErrors = signal<Record<number, string | null>>({});

  /** Fan out one request per tenant in the bag and merge. */
  async refresh(): Promise<void> {
    this.isLoading.set(true);
    const tenants = this.tokenBag.getAll();
    try {
      const results = await Promise.all(
        tenants.map(async (entry) => {
          try {
            const data = await this.fetchForTenant(entry);
            return { entry, data, error: null as string | null };
          } catch (err) {
            return {
              entry,
              data: null as YourEventsResponse | null,
              error: stringifyError(err)
            };
          }
        })
      );

      const merged: MergedYourEventsRow[] = [];
      const errors: Record<number, string | null> = {};
      for (const result of results) {
        if (result.error || !result.data) {
          errors[result.entry.tenantId] = result.error ?? 'unknown-error';
          continue;
        }
        const data = result.data;
        const tenantTheme = data.tenantTheme;
        const append = (segment: YourEventsSegment, events: typeof data.liveNow) => {
          for (const event of events) {
            merged.push({
              segment,
              tenantId: result.entry.tenantId,
              tenantSlug: result.entry.tenantSlug,
              tenantName: result.entry.tenantName,
              tenantTheme,
              event
            });
          }
        };
        append('liveNow', data.liveNow);
        append('today', data.today);
        append('upcoming', data.upcoming);
        append('past', data.past);
      }

      merged.sort((a, b) => {
        const segOrder: Record<YourEventsSegment, number> = {
          liveNow: 0, today: 1, upcoming: 2, past: 3
        };
        if (segOrder[a.segment] !== segOrder[b.segment]) {
          return segOrder[a.segment] - segOrder[b.segment];
        }
        const dirAsc = a.segment !== 'past';
        const aTs = Date.parse(a.event.startDateTime);
        const bTs = Date.parse(b.event.startDateTime);
        return dirAsc ? aTs - bTs : bTs - aTs;
      });

      this.rows.set(merged);
      this.tenantErrors.set(errors);
    } finally {
      this.isLoading.set(false);
    }
  }

  private async fetchForTenant(entry: PersistedTokenBagEntry): Promise<YourEventsResponse> {
    const url = `${this.tenantBaseUrl(entry)}/api/mobile/your-events`;

    // Token selection is tenant-specific, so the bearer MUST identify the
    // tenant we are fetching for — otherwise the BFF reads `tenant_id` from
    // the wrong token and returns the active tenant's events for every
    // section.
    //
    //  - Active tenant: send NO explicit Authorization header so the
    //    `authInterceptor` attaches the active access token AND owns the
    //    401 → refresh → replay path (single-flight). It also stamps the
    //    matching `X-Mobile-Device-Session`.
    //  - Non-active tenant: attach that tenant's freshly-rotated bearer +
    //    its own device-session header. The interceptor now respects a
    //    caller-set Authorization header and leaves both untouched.
    const isActive = entry.tenantId === this.tokenBag.getActive()?.tenantId;
    let headers = new HttpHeaders();
    if (!isActive) {
      const accessToken = await this.ensureFreshToken(entry);
      headers = new HttpHeaders({
        Authorization: `Bearer ${accessToken ?? ''}`,
        'X-Mobile-Device-Session': String(entry.deviceSessionId)
      });
    }

    const response = await firstValueFrom(
      this.http.get<ApiEnvelope<unknown>>(url, {
        headers
      }).pipe(
        // Never let a wedged/slow BFF hang the "Your events" screen forever.
        // Without this the page sits on "Loading events…" indefinitely; with
        // it the tenant is marked unavailable (graceful per-tenant error chip)
        // and the user can retry. 15s comfortably covers the BFF's own gRPC
        // (6s) + DB-fallback budget.
        timeout({ each: 15000 })
      )
    );
    return mapServerYourEvents(response.data);
  }

  /**
   * Non-active tenants don't pass through the standard `AuthService`
   * single-flight refresh queue (it scopes to the active tenant). For
   * a fan-out call we refresh the non-active tenant's token in place
   * using its persisted refresh token, then return the new access token.
   */
  private async ensureFreshToken(entry: PersistedTokenBagEntry): Promise<string | null> {
    try {
      const response = await firstValueFrom(
        this.http.post<ApiEnvelope<RefreshResponse>>(
          `${this.tenantBaseUrl(entry)}/api/auth/refresh`,
          { refreshToken: entry.refreshToken },
          { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) }
        )
      );
      const payload = response.data;
      await this.tokenBag.updateTenantRefresh(entry.tenantId, {
        refreshToken: payload.refreshToken,
        lastActiveAt: new Date().toISOString()
      });
      return payload.accessToken;
    } catch (err) {
      console.warn('[YourEventsService] Per-tenant refresh failed for tenantId=' + entry.tenantId, err);
      return null;
    }
  }

  /**
   * BFF base URL for a tenant. The mobile app uses a single flat host
   * (`environment.apiUrl`) for every tenant; the BFF resolves tenancy
   * from `tenant_id` in the JWT. See `resolveTenantBaseUrl` for the
   * historical context.
   */
  private tenantBaseUrl(entry: PersistedTokenBagEntry): string {
    return resolveTenantBaseUrl(environment.apiUrl, entry.tenantSlug);
  }
}

/**
 * Returns the mobile API base URL for a tenant.
 *
 * The mobile app is intentionally single-host: every tenant calls the
 * same `environment.apiUrl` (e.g. `https://api.myvirtrio.com`) and the
 * BFF resolves tenancy from `tenant_id` in the JWT. This matches the
 * staging cluster contract where `MobileBff__TenantSubdomain__Enabled`
 * is `false` (see `k8s/02-configmap.yaml`).
 *
 * Earlier revisions templated the tenant slug into the leading hostname
 * label (`api.myvirtrio.com` -> `acme.myvirtrio.com`). That required
 * wildcard DNS + a wildcard TLS cert + a wildcard ingress rule that the
 * staging cluster does not provision, so the per-tenant fan-out failed
 * with `ERR_NAME_NOT_RESOLVED`.
 *
 * The `slug` parameter is retained for signature stability across the
 * ~28 call sites and the dev proxy (`apps/mobile/proxy.conf.js`), which
 * still injects a `Host: <slug>.myvirtrio.cloud` header against the
 * local BFF regardless of the request URL.
 */
export function resolveTenantBaseUrl(apiUrl: string, _slug: string): string {
  return apiUrl;
}

function stringifyError(err: unknown): string {
  if (!err) return 'unknown-error';
  if (typeof err === 'string') return err;
  if (err instanceof Error) return err.message;
  const status = (err as { status?: number }).status;
  if (typeof status === 'number') return `http-${status}`;
  return 'unknown-error';
}

// ---------------------------------------------------------------------------
// Server <-> client YourEvents translator
//
// The BFF (vEvents.Mobile.Bff.Models.YourEventsResponseDto + YourEventCardDto)
// returns event cards as `{ name, ... }` while the mobile model
// (YourEventsSummary) names the field `eventName`. Field-name parity used
// to drift between the BFF and mobile (same drift as on AuthContext); we
// translate at the deserialisation boundary so `event.eventName` etc. just
// work in the renderer.
// ---------------------------------------------------------------------------

interface ServerYourEventCard {
  eventId?: number;
  registrationId?: number;
  name?: string;
  startDateTime?: string;
  endDateTime?: string;
  logoUrl?: string | null;
  bannerUrl?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
}

interface ServerTenantTheme {
  tenantId?: number;
  slug?: string;
  name?: string;
  logoUrl?: string | null;
  primaryColor?: string | null;
  faviconUrl?: string | null;
}

interface ServerYourEventsResponse {
  liveNow?: ServerYourEventCard[];
  today?: ServerYourEventCard[];
  upcoming?: ServerYourEventCard[];
  past?: ServerYourEventCard[];
  tenantTheme?: ServerTenantTheme;
  pastNextBefore?: string | null;
  nextBefore?: string | null;
}

function mapServerYourEvents(raw: unknown): YourEventsResponse {
  const r = (raw ?? {}) as ServerYourEventsResponse;
  const tenantTheme = mapTheme(r.tenantTheme);
  return {
    liveNow: (r.liveNow ?? []).map((c) => mapCard(c, tenantTheme)),
    today: (r.today ?? []).map((c) => mapCard(c, tenantTheme)),
    upcoming: (r.upcoming ?? []).map((c) => mapCard(c, tenantTheme)),
    past: (r.past ?? []).map((c) => mapCard(c, tenantTheme)),
    tenantTheme,
    nextBefore: r.pastNextBefore ?? r.nextBefore ?? null
  };
}

function mapCard(c: ServerYourEventCard, tenantTheme: import('@vevents/auth').AuthContextTheme): import('../models/your-events.models').YourEventsSummary {
  return {
    eventId: c.eventId ?? 0,
    registrationId: c.registrationId ?? null,
    eventName: c.name ?? '',
    startDateTime: c.startDateTime ?? '',
    endDateTime: c.endDateTime ?? null,
    logoUrl: c.logoUrl ?? null,
    bannerUrl: c.bannerUrl ?? null,
    theme: c.primaryColor || c.secondaryColor
      ? {
          ...tenantTheme,
          primaryColor: c.primaryColor ?? tenantTheme.primaryColor,
          accentColor: c.secondaryColor ?? tenantTheme.accentColor
        }
      : null
  };
}

function mapTheme(t: ServerTenantTheme | undefined): import('@vevents/auth').AuthContextTheme {
  return {
    primaryColor: t?.primaryColor ?? '#0f172a',
    accentColor: '#38bdf8',
    backgroundColor: '#ffffff',
    textColor: '#0f172a',
    logoUrl: t?.logoUrl ?? null,
    faviconUrl: t?.faviconUrl ?? null
  };
}
